import React from 'react';
import 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import { Box } from '@mui/material';
import { TypographySettings } from '../../../components/config';
import { formatNumber } from '../../../utilities';

const textColor = '#5b6b73';
const gridColor = 'rgba(24, 76, 97, 0.08)';

interface TooltipItem {
  index: number;
  datasetIndex: number;
  value: string;
}

interface TooltipData {
  labels: string[];
  datasets: { label: string; data: number[] }[];
}

const options = {
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    position: 'bottom',
    labels: { boxWidth: 10, fontSize: 12, fontColor: textColor, fontFamily: TypographySettings.DefaultFontFamily },
  },
  layout: { padding: { top: 24 } },
  scales: {
    xAxes: [
      {
        gridLines: { display: false, drawBorder: false },
        ticks: { autoSkip: false, maxRotation: 0, fontSize: 13, fontColor: textColor, fontFamily: TypographySettings.DefaultFontFamily },
        barPercentage: 0.9,
        categoryPercentage: 0.8,
        maxBarThickness: 32,
      },
    ],
    yAxes: [
      {
        gridLines: { color: gridColor, zeroLineColor: gridColor, drawBorder: false, drawTicks: false },
        ticks: { beginAtZero: true, precision: 0, padding: 8, maxTicksLimit: 5, fontSize: 12, fontColor: textColor, fontFamily: TypographySettings.DefaultFontFamily },
      },
    ],
  },
  tooltips: {
    backgroundColor: '#16323d',
    displayColors: false,
    xPadding: 10,
    yPadding: 8,
    titleFontFamily: TypographySettings.DefaultFontFamily,
    bodyFontFamily: TypographySettings.DefaultFontFamily,
    callbacks: {
      title: (items: TooltipItem[], data: TooltipData) => `${data.labels[items[0].index]} · ${data.datasets[items[0].datasetIndex].label}`,
      label: (item: TooltipItem, data: TooltipData) => {
        const citizens = Number(item.value);
        const total = data.datasets[item.datasetIndex].data.reduce((sum, value) => sum + value, 0);
        return `${formatNumber(citizens)} borgere (${formatNumber((citizens / total) * 100)} %)`;
      },
    },
  },
  plugins: {
    datalabels: {
      display: (context: { dataIndex: number; dataset: { data: number[] } }) => context.dataset.data[context.dataIndex] > 0,
      anchor: 'end',
      align: 'end',
      offset: 2,
      color: '#16323d',
      font: { size: 12, weight: 600, family: TypographySettings.DefaultFontFamily },
    },
  },
};

export interface InterventionLengthSeries {
  label: string;
  citizens: number[];
  barColor: string;
  hoverColor: string;
}

export interface InterventionLengthChartProps {
  labels: string[];
  series: InterventionLengthSeries[];
}

export function InterventionLengthChart({ labels, series }: InterventionLengthChartProps) {
  return (
    <Box sx={{ height: 300 }}>
      <Bar
        data={{
          labels,
          datasets: series.map(year => ({ label: year.label, data: year.citizens, backgroundColor: year.barColor, hoverBackgroundColor: year.hoverColor })),
        }}
        options={options}
      />
    </Box>
  );
}
